/**
 * Customer-facing order-status label.
 *
 * The backend `order_status_text` collapses the lifecycle into
 * five values (Pending, confirmed, picked, Delivered, Cancelled)
 * based on `order_status` alone. Once the driver drops the parcel
 * the row reads "Delivered" even though delivery docs haven't been
 * reviewed yet — so a customer sees "Delivered" the moment their
 * order lands in a driver-verified state, which is misleading given
 * they haven't been charged (under hold-until-approve) and the
 * driver hasn't been paid either.
 *
 * This helper adds a second layer: when the order is delivered but
 * docs aren't yet approved, we surface where in the review loop the
 * order actually is. Callers pass any object that has the two shape-
 * safe fields — works on OrderRow list rows and the fuller
 * order_details payload alike.
 *
 * Kinds:
 *   pending            — order_status 1, driver not yet confirmed
 *   confirmed          — order_status 2, driver on the way
 *   picked             — order_status 3, in transit
 *   pending_approval   — order_status 4, docs not yet reviewed
 *                        (verification_status 0 / 1)
 *   docs_rejected      — order_status 4, docs bounced back for
 *                        re-upload (verification_status 2)
 *   closed             — order_status 4, permanently closed
 *                        (verification_status 5) — customer will
 *                        never be charged, driver never credited
 *   delivered          — order_status 4, docs approved
 *                        (verification_status 4) — the actual
 *                        terminal success state
 *   cancelled          — status = 0
 *   unknown            — everything else
 */

export type OrderStatusKind =
  | 'pending'
  | 'confirmed'
  | 'picked'
  | 'pending_approval'
  | 'docs_rejected'
  | 'closed'
  | 'delivered'
  | 'cancelled'
  | 'unknown'

export interface OrderStatusInput {
  status?: number | null
  order_status?: number | null
  verification_status?: number | null | undefined
  /** Raw backend string ("Cancelled" | "Pending" | "Delivered" etc.) */
  order_status_text?: string | null
}

export interface OrderStatusView {
  kind: OrderStatusKind
  /** Human label shown on badges + hero pills. */
  label: string
  /** Optional secondary line for detail views. Empty when there's nothing worth adding. */
  hint: string
  /** Tone the badge/pill should paint in — maps to existing CSS classes. */
  tone: 'neutral' | 'pending' | 'active' | 'delivered' | 'warning' | 'danger' | 'cancelled'
}

export function orderStatusView(row: OrderStatusInput): OrderStatusView {
  const cancelled =
    row?.status === 0 ||
    String(row?.order_status_text || '').toLowerCase() === 'cancelled'
  if (cancelled) {
    return { kind: 'cancelled', label: 'Cancelled', hint: '', tone: 'cancelled' }
  }

  const os = Number(row?.order_status)
  const vs = row?.verification_status == null ? null : Number(row.verification_status)

  if (os === 1) return { kind: 'pending',   label: 'Pending',   hint: 'Waiting for a driver to accept the order.', tone: 'pending' }
  if (os === 2) return { kind: 'confirmed', label: 'Confirmed', hint: 'Driver is on the way to pickup.',           tone: 'active' }
  if (os === 3) return { kind: 'picked',    label: 'Picked up', hint: 'Order is in transit.',                       tone: 'active' }

  if (os === 4) {
    // Delivered by the driver — differentiate by doc verification state.
    if (vs === 4) {
      return { kind: 'delivered', label: 'Delivered', hint: 'Delivery documents approved.', tone: 'delivered' }
    }
    if (vs === 5) {
      return { kind: 'closed', label: 'Closed', hint: 'Order closed by Xobo Ops. No charge will be made.', tone: 'cancelled' }
    }
    if (vs === 2) {
      return { kind: 'docs_rejected', label: 'Docs rejected', hint: 'Delivery documents need attention.', tone: 'danger' }
    }
    // vs === 0 (Delivered, no docs yet), 1 (Docs Uploaded), or null (list
    // response from older backend that didn't include verification_status).
    // In all three cases the customer's most-honest status is "pending
    // approval" — the driver said dropped, but no one has verified yet.
    return {
      kind: 'pending_approval',
      label: 'Pending Approval',
      hint: 'Driver reported the delivery — waiting for Xobo Ops to review the delivery documents.',
      tone: 'warning',
    }
  }

  // Fallback — mirror whatever the backend text says so we don't lose signal.
  const raw = String(row?.order_status_text || '').trim()
  return { kind: 'unknown', label: raw || 'Unknown', hint: '', tone: 'neutral' }
}

/**
 * Map an OrderStatusView.tone to the existing OrderHistory row-badge
 * classes so the caller doesn't have to duplicate the switch. Kept as
 * a separate helper so components that use different badge chrome
 * (TrackOrder pill, PublicTrip pill) can build their own class map.
 */
export function orderHistoryBadgeClass(view: OrderStatusView): string {
  switch (view.tone) {
    case 'cancelled': return 'oh-badge--cancelled'
    case 'pending':   return 'oh-badge--pending'
    case 'active':    return 'oh-badge--active'
    case 'warning':   return 'oh-badge--warning'
    case 'danger':    return 'oh-badge--danger'
    case 'delivered': return 'oh-badge--delivered'
    default:          return 'oh-badge--default'
  }
}
